import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

// Register the necessary Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DrugConsumptionChart = ({ data }) => {
  const [timeRange, setTimeRange] = useState('year');  // Filter for week, month, or year
  const [selectedMedication, setSelectedMedication] = useState(null);  // Filter for specific medication

  // Initialize objects to store aggregated data for age group, gender, and medications
  const genderAgeGroupData = {
    'Infants': { Male: 0, Female: 0 },
    '0-18': { Male: 0, Female: 0 },
    '19-40': { Male: 0, Female: 0 },
    '41-60': { Male: 0, Female: 0 },
    '60+': { Male: 0, Female: 0 },
  };

  const medicationData = {};  // Store aggregated data for each medication

  // Process the data to calculate totals for each gender, age group, and medication
  data.forEach((patient) => {
    const totalMedication = patient.medications.reduce((sum, med) => {
      return sum + med.consumedQuantity;
    }, 0);

    const age = patient.age;  // Assuming `age` exists in the patient data
    const gender = patient.gender;
    const admissionDate = new Date(patient.admissionDate);

    // Filter by time range (week/month/year)
    if (timeRange === 'week' && !isWithinWeek(admissionDate)) return;
    if (timeRange === 'month' && !isWithinMonth(admissionDate)) return;
    if (timeRange === 'year' && !isWithinYear(admissionDate)) return;

    // Process by age group
    let ageGroup = '';
    if (age < 6) {
      ageGroup = 'Infants';  // Infants below 6 years
    } else if (age <= 18) {
      ageGroup = '0-18';
    } else if (age <= 40) {
      ageGroup = '19-40';
    } else if (age <= 60) {
      ageGroup = '41-60';
    } else {
      ageGroup = '60+';
    }

    genderAgeGroupData[ageGroup][gender] += totalMedication;

    // Process by medication
    patient.medications.forEach((med) => {
      if (!selectedMedication || med.medicationName === selectedMedication) {
        if (!medicationData[med.medicationName]) {
          medicationData[med.medicationName] = { Male: 0, Female: 0, 'Infants': 0, '0-18': 0, '19-40': 0, '41-60': 0, '60+': 0 };
        }

        medicationData[med.medicationName][gender] += med.consumedQuantity;
        medicationData[med.medicationName][ageGroup] += med.consumedQuantity;
      }
    });
  });

  // Convert the aggregated data for the chart
  const ageGroupLabels = Object.keys(genderAgeGroupData);
  const maleData = ageGroupLabels.map((group) => genderAgeGroupData[group].Male);
  const femaleData = ageGroupLabels.map((group) => genderAgeGroupData[group].Female);

  // Prepare the data for the chart, combining both gender/age and medication data
  const chartData = {
    labels: ageGroupLabels,  // X-axis: age groups
    datasets: [
      {
        label: 'Men',  // Label for male data
        data: maleData,  // Y-axis: male medications sold for each age group
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Women',  // Label for female data
        data: femaleData,  // Y-axis: female medications sold for each age group
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Prepare medication-specific datasets for the chart
  const medicationLabels = Object.keys(medicationData);
  medicationLabels.forEach((medicationName) => {
    // Only add the dataset once for each medication category (Men and Women combined)
    chartData.datasets.push({
      label: `${medicationName}`,  // Label for medication consumption (without separating by gender)
      data: ageGroupLabels.map((group) => medicationData[medicationName][group]),
      backgroundColor: 'rgba(54, 162, 235, 0.2)',  // Random color for each medication
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1,
    });
  });

  // Filter functions for time ranges
  function isWithinWeek(date) {
    const now = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(now.getDate() - 7);
    return date >= oneWeekAgo;
  }

  function isWithinMonth(date) {
    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(now.getMonth() - 1);
    return date >= oneMonthAgo;
  }

  function isWithinYear(date) {
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    return date >= oneYearAgo;
  }

  // Handle Time Range Change
  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
  };

  // Handle Medication Filter Change
  const handleMedicationChange = (e) => {
    setSelectedMedication(e.target.value);
  };

  // Options for the chart
  const options = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    plugins: {
      title: {
        display: true,
        text: 'Drug Consumption by Gender, Age Group, and Medication',
      },
      legend: {
        display: false,  // Disable the legend (no labels shown)
      },
    },
  };

  return (
    <div className="chart-container">
      <h3>Medication Sales by Gender, Age Group, and Medication</h3>
  
      {/* Time Range Filter */}
      <select onChange={handleTimeRangeChange} value={timeRange}>
        <option value="week">Last 1 Week</option>
        <option value="month">Last 1 Month</option>
        <option value="year">Last 1 Year</option>
      </select>
  
      {/* Medication Filter */}
      <select onChange={handleMedicationChange} className='select-option' value={selectedMedication || ''}>
        <option value="">All Medications</option>
        {medicationLabels.map((medication, index) => (
          <option key={index} value={medication}>
            {medication}
          </option>
        ))}
      </select>
  
      <Bar data={chartData} options={options} />
    </div>
  );
  
};

export default DrugConsumptionChart;
